import { ShellIconMenu } from '@zrsystem/ui';
import { useMemo } from 'react';

import { isShellNavGroupActive, isShellNavPathActive } from '@/shared/shell/nav-active.js';
import type { ShellNavEntry } from '@/shared/shell/nav.types.js';

const BOTTOM_NAV_PRIMARY_SLOTS = 4;

export interface ShellBottomNavProps {
  entries: readonly ShellNavEntry[];
  pathname: string;
  onNavigate: (path: string) => void;
  onOpenMenu: () => void;
}

function resolveBottomNavTarget(entry: ShellNavEntry): string | undefined {
  if (entry.kind === 'group') {
    return entry.defaultPath;
  }

  if (entry.enabled === false || entry.path === undefined) {
    return undefined;
  }

  return entry.path;
}

function isBottomNavEntryActive(pathname: string, entry: ShellNavEntry): boolean {
  if (entry.kind === 'group') {
    return isShellNavGroupActive(pathname, entry);
  }

  return isShellNavPathActive(pathname, entry);
}

export function ShellBottomNav({ entries, pathname, onNavigate, onOpenMenu }: ShellBottomNavProps) {
  const primaryEntries = useMemo(
    () =>
      entries
        .filter((entry) => resolveBottomNavTarget(entry) !== undefined)
        .slice(0, BOTTOM_NAV_PRIMARY_SLOTS),
    [entries],
  );

  return (
    <nav className="shell-bottom-nav" aria-label="Navegação principal">
      {primaryEntries.map((entry) => {
        const targetPath = resolveBottomNavTarget(entry);
        if (targetPath === undefined) {
          return null;
        }

        const Icon = entry.icon;
        const active = isBottomNavEntryActive(pathname, entry);
        const enabled = entry.kind === 'group' || entry.enabled !== false;

        return (
          <button
            key={entry.id}
            type="button"
            className="shell-bottom-nav__item zr-touch-target"
            data-active={active ? 'true' : 'false'}
            aria-label={entry.label}
            aria-current={active ? 'page' : undefined}
            disabled={!enabled}
            title={enabled ? entry.label : `${entry.label} (em breve)`}
            onClick={() => {
              if (!enabled) {
                return;
              }
              onNavigate(targetPath);
            }}
          >
            <span className="shell-bottom-nav__icon" aria-hidden>
              <Icon size={18} />
            </span>
            <span className="shell-bottom-nav__label">{entry.label}</span>
          </button>
        );
      })}

      <button
        type="button"
        className="shell-bottom-nav__item zr-touch-target"
        aria-label="Abrir menu completo"
        title="Menu"
        onClick={onOpenMenu}
      >
        <span className="shell-bottom-nav__icon" aria-hidden>
          <ShellIconMenu size={18} />
        </span>
        <span className="shell-bottom-nav__label">Menu</span>
      </button>
    </nav>
  );
}
